import { create } from 'zustand'
import { useAuthStore } from './authStore'

// Función helper para manejar la expiración de sesión
const handleApiError = (error) => {
  if (error.message.includes('401')) {
    // Limpiar el estado de autenticación
    useAuthStore.getState().logout()
    // Redirigir a la página de login
    window.location.href = '/login?session_expired=true'
  }
}

// Estructura por defecto como fallback
export const DEFAULT_DIMENSIONES_CRITERIOS = {
  'I. DIMENSIÓN DOCENCIA Y RESULTADOS DEL PROCESO FORMATIVO': [
    {
      nombre: 'Criterio 1. Modelo educativo y diseño curricular',
      descripcion: 'La formulación del modelo educativo define las características y objetivos de los programas'
    },
    {
      nombre: 'Criterio 2. Procesos y resultados de enseñanza',
      descripcion: 'El diseño e implementación de los programas de enseñanza y aprendizaje'
    }
  ],
  'II. DIMENSIÓN GESTIÓN ESTRATÉGICA Y RECURSOS INSTITUCIONALES': [
    {
      nombre: 'Criterio 5. Gobierno y estructura organizacional',
      descripcion: 'La universidad cuenta con un sistema de gobierno y una estructura organizacional'
    }
  ]
}

const useEvidenceStore = create((set, get) => ({
  evidences: [],
  formStructure: null,
  isLoading: false,
  isLoadingStructure: false,
  error: null,
  uploadProgress: 0,

  // Obtener evidencias del usuario
  fetchUserEvidences: async (token) => {
    set({ isLoading: true, error: null })
    
    try {
      console.log('🔄 Cargando evidencias del usuario...')
      console.log('Token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN')
      
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/evidences/user' : '/api/evidences/user'
      console.log('URL:', apiUrl)
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)

      const data = await response.json()
      console.log('Response data:', data)

      if (!response.ok) {
        throw new Error(`${response.status}: ${data.error || 'Error al cargar evidencias'}`)
      }

      console.log('✅ Evidencias cargadas:', data.evidences.length)
      set({ evidences: data.evidences, isLoading: false })
    } catch (error) {
      console.error('❌ Error cargando evidencias:', error)
      handleApiError(error)
      set({ error: error.message, isLoading: false })
    }
  },

  // Obtener todas las evidencias (admin)
  fetchAllEvidences: async (token, filters = {}) => {
    set({ isLoading: true, error: null })
    
    try {
      const queryParams = new URLSearchParams(filters).toString()
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/evidences/all' : '/api/evidences/all'
      const response = await fetch(`${apiUrl}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(`${response.status}: ${data.error || 'Error al cargar evidencias'}`)
      }

      set({ evidences: data.evidences, isLoading: false })
    } catch (error) {
      handleApiError(error)
      set({ error: error.message, isLoading: false })
    }
  },

  // Subir evidencia
  uploadEvidence: async (token, files, dimension, criterio, year = null, missingText = null) => {
    set({ isLoading: true, error: null, uploadProgress: 0 })
    
    try {
      // Si hay archivos, subirlos
      if (files && files.length > 0) {
        const uploadPromises = files.map(async (file) => {
          // Crear FormData para enviar el archivo
          const formData = new FormData()
          formData.append('file', file)
          formData.append('dimension', dimension)
          formData.append('criterio', criterio)
          if (year) formData.append('year', year)
          if (missingText) formData.append('missingText', missingText)

          // Subir archivo directamente al servidor
          const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/evidences/upload' : '/api/evidences/upload'
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData
          })

          if (!response.ok) {
            let errorDetails = 'Error al subir archivo';
            try {
                const errorData = await response.json();
                if (errorData.details) {
                    errorDetails = errorData.details;
                } else if (errorData.error) {
                    errorDetails = errorData.error;
                }
            } catch (e) {
                errorDetails = response.statusText;
            }
            throw new Error(`${response.status}: ${errorDetails}`);
          }

          const data = await response.json()

          return data
        })

        await Promise.all(uploadPromises)
      } 
      // Si no hay archivos pero hay texto de evidencia faltante
      else if (missingText && missingText.trim()) {
        const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/evidences/record-metadata' : '/api/evidences/record-metadata'
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            dimension,
            criterio,
            year,
            missingText
          })
        })

        const data = await response.json()
        if (!response.ok) {
          throw new Error(`${response.status}: ${data.error || 'Error al registrar evidencia faltante'}`)
        }
      }
      
      set({ isLoading: false, uploadProgress: 100 })
      
      // Recargar evidencias
      await get().fetchUserEvidences(token)
      
      return { success: true }
    } catch (error) {
      handleApiError(error)
      set({ error: error.message, isLoading: false, uploadProgress: 0 })
      return { success: false, error: error.message }
    }
  },

  // Eliminar evidencias
  deleteEvidences: async (token, evidenceIds) => {
    set({ isLoading: true, error: null })
    
    try {
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/evidences/delete' : '/api/evidences/delete'
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ evidenceIds }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(`${response.status}: ${data.error || 'Error al eliminar evidencias'}`)
      }

      // Actualizar estado local
      const currentEvidences = get().evidences
      const updatedEvidences = currentEvidences.filter(
        evidence => !evidenceIds.includes(evidence.id)
      )
      
      set({ evidences: updatedEvidences, isLoading: false })
      
      return { success: true }
    } catch (error) {
      handleApiError(error)
      set({ error: error.message, isLoading: false })
      return { success: false, error: error.message }
    }
  },

  // Obtener estructura del formulario desde el backend
  fetchFormStructure: async (forceRefresh = false, userEmail = null) => {
    set({ isLoadingStructure: true, error: null })
    
    try {
      const params = new URLSearchParams()
      if (forceRefresh) params.append('refresh', 'true')
      if (userEmail) params.append('email', userEmail)
      
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/form-structure' : '/api/form-structure'
      const url = `${apiUrl}${params.toString() ? '?' + params.toString() : ''}`
        
      const response = await fetch(url)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Error obteniendo estructura del formulario')
      }

      set({ 
        formStructure: data.structure,
        isLoadingStructure: false,
        error: null 
      })

      return { success: true, meta: data.meta }
    } catch (error) {
      console.error('Error obteniendo estructura:', error)
      set({ 
        formStructure: DEFAULT_DIMENSIONES_CRITERIOS,
        isLoadingStructure: false,
        error: error.message 
      })
      return { success: false, error: error.message }
    }
  },

  // Limpiar cache de estructura (solo admin)
  refreshFormStructure: async (token) => {
    try {
      const apiUrl = import.meta.env.DEV ? 'http://localhost:3001/api/form-structure' : '/api/form-structure'
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Recargar estructura después de limpiar cache
        return await get().fetchFormStructure(true)
      }
      
      return { success: false, error: 'No autorizado para limpiar cache' }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  clearError: () => set({ error: null }),
}))

export { useEvidenceStore }
